import { Outlet } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import {
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { RightSidebarProvider } from "@/contexts/RightSidebarContext";
import { PageHeaderProvider } from "@/contexts/PageHeaderContext";
import { RightSidebar } from "@/components/layout/RightSidebar";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { MobilePageNav } from "@/components/layout/MobilePageNav";
import { usePageHeaderContext } from "@/contexts/PageHeaderContext";
import {
  PROJECT_SWITCHER_OPEN_EVENT,
  ProjectSwitcher,
} from "@/components/ProjectSwitcher";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { OfflineStatusBadge } from "@/components/OfflineStatusBadge";
import { OfflineEditGuard } from "@/components/OfflineEditGuard";
import { NotificationsProvider } from "@/hooks/useNotifications";

function DesktopPageTitle() {
  const { title } = usePageHeaderContext();
  if (!title) return null;
  return (
    <span className="truncate text-sm font-medium text-foreground">
      {title}
    </span>
  );
}

export function MainLayout() {
  const openProjectSwitcher = () => {
    window.dispatchEvent(new Event(PROJECT_SWITCHER_OPEN_EVENT));
  };

  return (
    <SidebarProvider className="h-dvh overflow-hidden">
      <RightSidebarProvider>
        <PageHeaderProvider>
          <NotificationsProvider>
            <div className="h-dvh flex w-full overflow-hidden">
              <ProjectSwitcher />
              <AppSidebar />
              <SidebarInset className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <header className="flex h-12 md:h-14 shrink-0 items-center gap-2 border-b border-border px-3 md:px-4">
                  <SidebarTrigger className="-ml-1" />
                  <Separator orientation="vertical" className="h-6" />
                  <div className="flex-1 flex items-center min-w-0 md:hidden">
                    <MobilePageNav />
                  </div>
                  <div className="hidden md:flex flex-1 items-center min-w-0">
                    <DesktopPageTitle />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openProjectSwitcher}
                    allowOffline
                    className="hidden md:inline-flex items-center gap-2"
                  >
                    <Search className="h-4 w-4" />
                    <span>Search</span>
                    <span className="text-xs text-muted-foreground">Cmd+K</span>
                  </Button>
                  <OfflineStatusBadge />
                  <NotificationBell />
                </header>
                <main className="flex-1 overflow-auto overscroll-contain p-3 md:p-6">
                  <OfflineEditGuard>
                    <Outlet />
                  </OfflineEditGuard>
                </main>
              </SidebarInset>
              <RightSidebar />
            </div>
          </NotificationsProvider>
        </PageHeaderProvider>
      </RightSidebarProvider>
    </SidebarProvider>
  );
}
